/**
 * StudioPanel.tsx
 * 
 * The right-side panel in Studio mode. Extends AgentSidePanel with extra tabs:
 * Tasks | Memory | Triggers | Apps | Interfaces | Terminal | Files
 * 
 * The Terminal and Files tabs reuse the existing AgentTerminalPanel content.
 */
import { useState, useEffect, useCallback } from 'react'
import {
  X, CheckSquare, Brain, Zap, AppWindow, Layout,
  Terminal, FileText, Monitor, Plus,
  Loader2, AlertCircle, CheckCircle2, Circle,
  Globe, Trash2, ExternalLink, RefreshCw, ToggleLeft, ToggleRight,
  Mail, Calendar, Clock, Webhook, Play
} from 'lucide-react'
import type { UserMemory } from '@/types'
import type { AgentState } from '@/hooks/useAgent'
import type { StudioTab } from '@/App'
import { useNav } from '@/App'
import { useApp } from '@/store/AppContext'
import {
  interfacesApi, triggersApi, appsApi,
  type Interface, type Trigger, type App
} from '@/services/studioService'

// ── Sub-panel: Tasks ──────────────────────────────────────────
function TasksPanel({ agentState }: { agentState: AgentState }) {
  const tasks = agentState.liveTasks ?? []
  const isActive = ['planning', 'running'].includes(agentState.status)

  if (tasks.length === 0) {
    return (
      <div className="studio-empty">
        <CheckSquare size={28} className="studio-empty-icon" />
        <p className="studio-empty-title">Ingen aktive oppgaver</p>
        <p className="studio-empty-hint">
          Start agenten for å se oppgaver her i sanntid.
        </p>
      </div>
    )
  }

  const done = tasks.filter(t => t.status === 'done').length
  const total = tasks.length
  const pct = total > 0 ? Math.round((done / total) * 100) : 0

  return (
    <div className="studio-tasks">
      <div className="studio-tasks-progress">
        <div className="studio-tasks-progress-bar">
          <div className="studio-tasks-progress-fill" style={{ width: `${pct}%` }} />
        </div>
        <span className="studio-tasks-progress-label">{done}/{total} fullført</span>
      </div>
      <div className="studio-tasks-list">
        {tasks.map(task => (
          <div key={task.id} className={`studio-task-item studio-task-${task.status}`}>
            <div className="studio-task-icon">
              {task.status === 'done' && <CheckCircle2 size={14} />}
              {task.status === 'running' && <Loader2 size={14} className="studio-spin" />}
              {task.status === 'error' && <AlertCircle size={14} />}
              {task.status !== 'done' && task.status !== 'running' && task.status !== 'error' && <Circle size={14} />}
            </div>
            <div className="studio-task-content">
              <span className="studio-task-label">{task.label}</span>
              {task.tool && <span className="studio-task-tool">{task.tool}</span>}
            </div>
          </div>
        ))}
      </div>
      {isActive && (
        <div className="studio-tasks-running-indicator">
          <div className="studio-tasks-pulse" />
          <span>Agent kjører...</span>
        </div>
      )}
    </div>
  )
}

// ── Sub-panel: Memory ─────────────────────────────────────────
function MemoryPanel({
  memory,
  onAdd,
  onRemove,
}: {
  memory: UserMemory[]
  onAdd: (key: string, value: string) => void
  onRemove: (id: string) => void
}) {
  const [showForm, setShowForm] = useState(false)
  const [key, setKey] = useState('')
  const [value, setValue] = useState('')

  const handleAdd = () => {
    if (key.trim() && value.trim()) {
      onAdd(key.trim(), value.trim())
      setKey('')
      setValue('')
      setShowForm(false)
    }
  }

  return (
    <div className="studio-memory">
      <div className="studio-section-header">
        <span className="studio-section-title">Minne ({memory.length})</span>
        <button className="studio-icon-btn" onClick={() => setShowForm(v => !v)} title="Legg til minne">
          <Plus size={13} />
        </button>
      </div>

      {showForm && (
        <div className="studio-memory-form">
          <input
            className="studio-input"
            placeholder="Nøkkel (f.eks. programmeringsspråk)"
            value={key}
            onChange={e => setKey(e.target.value)}
          />
          <input
            className="studio-input"
            placeholder="Verdi (f.eks. Python)"
            value={value}
            onChange={e => setValue(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
          />
          <div className="studio-memory-form-actions">
            <button className="studio-btn-ghost" onClick={() => setShowForm(false)}>Avbryt</button>
            <button className="studio-btn-primary" onClick={handleAdd}>Lagre</button>
          </div>
        </div>
      )}

      {memory.length === 0 ? (
        <div className="studio-empty">
          <Brain size={28} className="studio-empty-icon" />
          <p className="studio-empty-title">Ingen lagret minne</p>
          <p className="studio-empty-hint">
            Fortell Sine om deg selv — den husker det på tvers av samtaler.
          </p>
        </div>
      ) : (
        <div className="studio-memory-list">
          {memory.map(item => (
            <div key={item.id} className="studio-memory-item">
              <div className="studio-memory-item-content">
                <span className="studio-memory-key">{item.key}</span>
                <span className="studio-memory-value">{item.value}</span>
              </div>
              <button
                className="studio-icon-btn studio-icon-btn-danger"
                onClick={() => onRemove(item.id)}
                title="Fjern"
              >
                <X size={11} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Trigger type icon ─────────────────────────────────────────
function TriggerTypeIcon({ type }: { type: string }) {
  switch (type) {
    case 'gmail': return <Mail size={13} />
    case 'calendar': return <Calendar size={13} />
    case 'schedule': return <Clock size={13} />
    case 'webhook': return <Webhook size={13} />
    default: return <Play size={13} />
  }
}

// ── Sub-panel: Triggers ───────────────────────────────────────
function TriggersPanel() {
  const [triggers, setTriggers] = useState<Trigger[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [newName, setNewName] = useState('')
  const [newType, setNewType] = useState<string>('gmail')
  const [creating, setCreating] = useState(false)

  const load = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await triggersApi.list()
      setTriggers(data)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Feil ved lasting')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  // Listen for trigger-created events from TriggerSuggestionCard in chat
  useEffect(() => {
    const handleTriggerCreated = async (e: Event) => {
      const detail = (e as CustomEvent).detail
      if (!detail) return
      try {
        // Build trigger_config from the suggestion data
        const triggerConfig: Record<string, unknown> = {}
        if (detail.gmail_filter) triggerConfig.gmail_filter = detail.gmail_filter
        if (detail.schedule) triggerConfig.schedule = detail.schedule
        // Build action_config
        const actionConfig: Record<string, unknown> = {}
        if (detail.action?.prompt) actionConfig.prompt = detail.action.prompt
        if (detail.action?.reply_template) actionConfig.reply_template = detail.action.reply_template
        if (detail.workflow_spec) actionConfig.workflow_spec = detail.workflow_spec
        const created = await triggersApi.create({
          name: detail.name,
          description: detail.description,
          trigger_type: detail.trigger_type,
          trigger_config: triggerConfig,
          action_type: detail.action?.type || 'run_agent',
          action_config: actionConfig,
          project_id: undefined,
        })
        setTriggers(prev => [created, ...prev])
      } catch (err) {
        console.error('[TriggersPanel] Failed to create trigger from suggestion:', err)
        // Reload to show any partial state
        load()
      }
    }
    window.addEventListener('sine:trigger-created', handleTriggerCreated)
    return () => window.removeEventListener('sine:trigger-created', handleTriggerCreated)
  }, [load])

  const handleToggle = async (id: string) => {
    try {
      const result = await triggersApi.toggle(id)
      setTriggers(prev => prev.map(t => t.id === id ? { ...t, is_active: result.is_active } : t))
    } catch { /* ignore */ }
  }

  const handleDelete = async (id: string) => {
    try {
      await triggersApi.delete(id)
      setTriggers(prev => prev.filter(t => t.id !== id))
    } catch { /* ignore */ }
  }

  const handleCreate = async () => {
    if (!newName.trim()) return
    setCreating(true)
    try {
      const trigger = await triggersApi.create({
        name: newName.trim(),
        trigger_type: newType,
        trigger_config: {},
        action_type: 'run_agent',
        action_config: {},
      })
      setTriggers(prev => [trigger, ...prev])
      setNewName('')
      setShowForm(false)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Feil ved oppretting')
    } finally {
      setCreating(false)
    }
  }

  return (
    <div className="studio-triggers">
      <div className="studio-section-header">
        <span className="studio-section-title">Triggers ({triggers.length})</span>
        <div style={{ display: 'flex', gap: 4 }}>
          <button className="studio-icon-btn" onClick={load} title="Oppdater">
            <RefreshCw size={12} />
          </button>
          <button className="studio-icon-btn" onClick={() => setShowForm(v => !v)} title="Ny trigger">
            <Plus size={13} />
          </button>
        </div>
      </div>

      {showForm && (
        <div className="studio-memory-form">
          <input
            className="studio-input"
            placeholder="Navn på trigger"
            value={newName}
            onChange={e => setNewName(e.target.value)}
          />
          <select
            className="studio-input"
            value={newType}
            onChange={e => setNewType(e.target.value)}
          >
            <option value="gmail">Gmail</option>
            <option value="calendar">Kalender</option>
            <option value="schedule">Tidsplan</option>
            <option value="webhook">Webhook</option>
            <option value="manual">Manuell</option>
          </select>
          <div className="studio-memory-form-actions">
            <button className="studio-btn-ghost" onClick={() => setShowForm(false)}>Avbryt</button>
            <button className="studio-btn-primary" onClick={handleCreate} disabled={creating}>
              {creating ? <Loader2 size={12} className="studio-spin" /> : 'Opprett'}
            </button>
          </div>
        </div>
      )}

      {loading && (
        <div className="studio-loading">
          <Loader2 size={18} className="studio-spin" />
          <span>Laster triggers...</span>
        </div>
      )}

      {error && (
        <div className="studio-error">
          <AlertCircle size={14} />
          <span>{error}</span>
        </div>
      )}

      {!loading && !error && triggers.length === 0 && (
        <div className="studio-empty">
          <Zap size={28} className="studio-empty-icon" />
          <p className="studio-empty-title">Ingen triggers satt opp</p>
          <p className="studio-empty-hint">
            Koble til Gmail, kalender eller andre tjenester for å utløse agenten automatisk.
          </p>
          <button className="studio-btn-outline" style={{ marginTop: 12 }} onClick={() => setShowForm(true)}>
            <Plus size={12} style={{ marginRight: 4 }} />
            Opprett trigger
          </button>
        </div>
      )}

      {!loading && triggers.length > 0 && (
        <div className="studio-list">
          {triggers.map(trigger => (
            <div key={trigger.id} className="studio-list-item">
              <div className="studio-list-item-icon">
                <TriggerTypeIcon type={trigger.trigger_type} />
              </div>
              <div className="studio-list-item-content">
                <span className="studio-list-item-name">{trigger.name}</span>
                <span className="studio-list-item-meta">
                  {trigger.trigger_type} · {trigger.trigger_count} kjøringer
                </span>
              </div>
              <div className="studio-list-item-actions">
                <button
                  className="studio-icon-btn"
                  onClick={() => handleToggle(trigger.id)}
                  title={trigger.is_active ? 'Deaktiver' : 'Aktiver'}
                >
                  {trigger.is_active
                    ? <ToggleRight size={16} style={{ color: '#4ade80' }} />
                    : <ToggleLeft size={16} style={{ color: '#6b7280' }} />
                  }
                </button>
                <button
                  className="studio-icon-btn studio-icon-btn-danger"
                  onClick={() => handleDelete(trigger.id)}
                  title="Slett"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Sub-panel: Apps ───────────────────────────────────────────
function AppsPanel() {
  const [apps, setApps] = useState<App[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await appsApi.list()
      setApps(data)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Feil ved lasting')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const handleDelete = async (id: string) => {
    try {
      await appsApi.delete(id)
      setApps(prev => prev.filter(a => a.id !== id))
    } catch { /* ignore */ }
  }

  const handlePublish = async (id: string) => {
    try {
      const result = await appsApi.publish(id)
      setApps(prev => prev.map(a => a.id === id ? { ...a, is_published: true, published_url: result.url } : a))
    } catch { /* ignore */ }
  }

  return (
    <div className="studio-apps">
      <div className="studio-section-header">
        <span className="studio-section-title">Apper ({apps.length})</span>
        <button className="studio-icon-btn" onClick={load} title="Oppdater">
          <RefreshCw size={12} />
        </button>
      </div>

      {loading && (
        <div className="studio-loading">
          <Loader2 size={18} className="studio-spin" />
          <span>Laster apper...</span>
        </div>
      )}

      {error && (
        <div className="studio-error">
          <AlertCircle size={14} />
          <span>{error}</span>
        </div>
      )}

      {!loading && !error && apps.length === 0 && (
        <div className="studio-empty">
          <AppWindow size={28} className="studio-empty-icon" />
          <p className="studio-empty-title">Ingen apper ennå</p>
          <p className="studio-empty-hint">
            Be agenten bygge en app, og den vil vises her med live preview.
          </p>
        </div>
      )}

      {!loading && apps.length > 0 && (
        <div className="studio-list">
          {apps.map(app => (
            <div key={app.id} className="studio-list-item">
              <div className="studio-list-item-icon" style={{ fontSize: 16 }}>
                {app.icon}
              </div>
              <div className="studio-list-item-content">
                <span className="studio-list-item-name">{app.name}</span>
                <span className="studio-list-item-meta">
                  {app.is_published ? '✓ Publisert' : 'Upublisert'} · {app.view_count} visninger
                </span>
              </div>
              <div className="studio-list-item-actions">
                {app.is_published && app.published_url && (
                  <a
                    href={app.published_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="studio-icon-btn"
                    title="Åpne"
                  >
                    <ExternalLink size={12} />
                  </a>
                )}
                {!app.is_published && (
                  <button
                    className="studio-icon-btn"
                    onClick={() => handlePublish(app.id)}
                    title="Publiser"
                  >
                    <Globe size={12} />
                  </button>
                )}
                <button
                  className="studio-icon-btn studio-icon-btn-danger"
                  onClick={() => handleDelete(app.id)}
                  title="Slett"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Sub-panel: Interfaces ─────────────────────────────────────
function InterfacesPanel() {
  const [interfaces, setInterfaces] = useState<Interface[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [previewId, setPreviewId] = useState<string | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [sandboxLoading, setSandboxLoading] = useState(false)
  const { activeConversation } = useApp()

  const load = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      // Only load interfaces for the active conversation
      const data = await interfacesApi.list(activeConversation?.id)
      setInterfaces(data)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Feil ved lasting')
    } finally {
      setLoading(false)
    }
  }, [activeConversation?.id])

  useEffect(() => { load() }, [load])

  // Listen for interface-created events from InterfaceSuggestionCard in chat
  // Auto-show preview when a new interface is built
  useEffect(() => {
    const handleInterfaceCreated = (e: Event) => {
      const detail = (e as CustomEvent).detail as { id: string; preview_url?: string; conversation_id?: string }
      // Only handle if it belongs to the active conversation (or no filter)
      if (detail.conversation_id && activeConversation?.id && detail.conversation_id !== activeConversation.id) return
      load().then(() => {
        // Auto-show preview if we got a preview_url directly
        if (detail.preview_url) {
          setPreviewId(detail.id)
          setPreviewUrl(detail.preview_url)
        }
      })
    }
    window.addEventListener('sine:interface-created', handleInterfaceCreated)
    return () => window.removeEventListener('sine:interface-created', handleInterfaceCreated)
  }, [load, activeConversation?.id])

  const handleDelete = async (id: string) => {
    try {
      await interfacesApi.delete(id)
      setInterfaces(prev => prev.filter(i => i.id !== id))
      if (previewId === id) {
        setPreviewId(null)
        setPreviewUrl(null)
      }
    } catch { /* ignore */ }
  }

  const handlePreview = async (iface: Interface) => {
    if (previewId === iface.id) {
      setPreviewId(null)
      setPreviewUrl(null)
      return
    }

    // Use existing sandbox URL if still valid
    if (iface.sandbox_url && iface.sandbox_expires_at) {
      const expires = new Date(iface.sandbox_expires_at)
      if (expires > new Date()) {
        setPreviewId(iface.id)
        setPreviewUrl(iface.sandbox_url)
        return
      }
    }

    // Create new sandbox
    setSandboxLoading(true)
    try {
      const result = await interfacesApi.createSandbox(iface.id)
      setPreviewId(iface.id)
      setPreviewUrl(result.sandbox_url)
      setInterfaces(prev => prev.map(i => i.id === iface.id
        ? { ...i, sandbox_url: result.sandbox_url, sandbox_expires_at: result.expires_at }
        : i
      ))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Sandbox-feil')
    } finally {
      setSandboxLoading(false)
    }
  }

  return (
    <div className="studio-interfaces">
      <div className="studio-section-header">
        <span className="studio-section-title">Grensesnitt ({interfaces.length})</span>
        <button className="studio-icon-btn" onClick={load} title="Oppdater">
          <RefreshCw size={12} />
        </button>
      </div>

      {loading && (
        <div className="studio-loading">
          <Loader2 size={18} className="studio-spin" />
          <span>Laster grensesnitt...</span>
        </div>
      )}

      {error && (
        <div className="studio-error">
          <AlertCircle size={14} />
          <span>{error}</span>
        </div>
      )}

      {!loading && !error && interfaces.length === 0 && (
        <div className="studio-empty">
          <Layout size={28} className="studio-empty-icon" />
          <p className="studio-empty-title">Ingen grensesnitt ennå</p>
          <p className="studio-empty-hint">
            Be agenten lage en nettside eller app — du kan se og redigere den live her.
          </p>
        </div>
      )}

      {!loading && interfaces.length > 0 && (
        <div className="studio-interfaces-list">
          {interfaces.map(iface => (
            <div key={iface.id} className="studio-interface-item">
              <div className="studio-list-item">
                <div className="studio-list-item-icon">
                  <Layout size={13} />
                </div>
                <div className="studio-list-item-content">
                  <span className="studio-list-item-name">{iface.name}</span>
                  <span className="studio-list-item-meta">
                    {iface.framework} · {iface.files?.length ?? 0} filer
                    {iface.status === 'error' && ' · ⚠ Feil'}
                  </span>
                </div>
                <div className="studio-list-item-actions">
                  <button
                    className={`studio-icon-btn${previewId === iface.id ? ' active' : ''}`}
                    onClick={() => handlePreview(iface)}
                    title="Live preview"
                    disabled={sandboxLoading}
                  >
                    {sandboxLoading && previewId !== iface.id
                      ? <Loader2 size={12} className="studio-spin" />
                      : <Monitor size={12} />
                    }
                  </button>
                  <button
                    className="studio-icon-btn studio-icon-btn-danger"
                    onClick={() => handleDelete(iface.id)}
                    title="Slett"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>

              {/* Live preview iframe */}
              {previewId === iface.id && previewUrl && (
                <div className="studio-interface-preview">
                  <div className="studio-interface-preview-bar">
                    <span className="studio-interface-preview-url">{previewUrl}</span>
                    <a
                      href={previewUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="studio-icon-btn"
                      title="Åpne i ny fane"
                    >
                      <ExternalLink size={11} />
                    </a>
                    <button
                      className="studio-icon-btn"
                      onClick={() => { setPreviewId(null); setPreviewUrl(null) }}
                      title="Lukk preview"
                    >
                      <X size={11} />
                    </button>
                  </div>
                  <iframe
                    src={previewUrl}
                    className="studio-interface-iframe"
                    title={iface.name}
                    sandbox="allow-scripts allow-same-origin allow-forms"
                  />
                </div>
              )}

              {sandboxLoading && previewId === null && (
                <div className="studio-loading" style={{ padding: '8px 0' }}>
                  <Loader2 size={14} className="studio-spin" />
                  <span>Starter sandbox...</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Tab definitions ───────────────────────────────────────────
const STUDIO_TABS: { id: StudioTab; label: string; icon: React.ReactNode }[] = [
  { id: 'tasks',      label: 'Oppgaver',    icon: <CheckSquare size={11} /> },
  { id: 'memory',     label: 'Minne',       icon: <Brain size={11} /> },
  { id: 'triggers',   label: 'Triggers',    icon: <Zap size={11} /> },
  { id: 'apps',       label: 'Apper',       icon: <AppWindow size={11} /> },
  { id: 'interfaces', label: 'Grensesnitt', icon: <Layout size={11} /> },
  { id: 'terminal',   label: 'Terminal',    icon: <Terminal size={11} /> },
  { id: 'files',      label: 'Filer',       icon: <FileText size={11} /> },
]

// ── Props ─────────────────────────────────────────────────────
interface StudioPanelProps {
  agentState: AgentState
  memory: UserMemory[]
  onAddMemory: (key: string, value: string) => void
  onRemoveMemory: (id: string) => void
  onClose: () => void
  terminalContent?: React.ReactNode
  filesContent?: React.ReactNode
}

export function StudioPanel({
  agentState,
  memory,
  onAddMemory,
  onRemoveMemory,
  onClose,
  terminalContent,
  filesContent,
}: StudioPanelProps) {
  const { studioTab, setStudioTab } = useNav()

  return (
    <div className="studio-panel">
      {/* ── Header ─────────────────────────────────────────── */}
      <div className="studio-panel-header">
        <div className="studio-panel-title">
          <Monitor size={14} style={{ color: '#9A9A9A' }} />
          <span>Studio</span>
        </div>

        {/* Tab bar — scrollable */}
        <div className="studio-panel-tabs">
          {STUDIO_TABS.map(tab => (
            <button
              key={tab.id}
              className={`studio-tab${studioTab === tab.id ? ' active' : ''}`}
              onClick={() => setStudioTab(tab.id)}
              title={tab.label}
            >
              {tab.icon}
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        <button className="studio-panel-close" onClick={onClose}>
          <X size={14} />
        </button>
      </div>

      {/* ── Body ───────────────────────────────────────────── */}
      <div className="studio-panel-body">
        {studioTab === 'tasks' && (
          <TasksPanel agentState={agentState} />
        )}
        {studioTab === 'memory' && (
          <MemoryPanel
            memory={memory}
            onAdd={onAddMemory}
            onRemove={onRemoveMemory}
          />
        )}
        {studioTab === 'triggers' && <TriggersPanel />}
        {studioTab === 'apps' && <AppsPanel />}
        {studioTab === 'interfaces' && <InterfacesPanel />}
        {studioTab === 'terminal' && (
          <div className="studio-passthrough">
            {terminalContent ?? (
              <div className="studio-empty">
                <Terminal size={28} className="studio-empty-icon" />
                <p className="studio-empty-title">Terminal</p>
                <p className="studio-empty-hint">Start agenten for å se terminal-output her.</p>
              </div>
            )}
          </div>
        )}
        {studioTab === 'files' && (
          <div className="studio-passthrough">
            {filesContent ?? (
              <div className="studio-empty">
                <FileText size={28} className="studio-empty-icon" />
                <p className="studio-empty-title">Filer</p>
                <p className="studio-empty-hint">Agentens genererte filer vises her.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
